import React, { useContext } from "react";

import { faBuilding, faClock, faFileAlt, faStar, faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import ViewportContext from "~/common/ViewportContext";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";

import { SearchBox } from "./SearchBox";

import styles from "./css/category.module.css";

export enum FixedCategory {
	ALL = "All templates",
	STARRED = "Starred templates",
	RECENT = "Recently used",
	MY = "My templates",
	WORKSPACE = "Workspace templates",
}

export const CategoryToPath: { [key: string]: string } = {
	[FixedCategory.ALL]: "all",
	[FixedCategory.MY]: "my",
	[FixedCategory.STARRED]: "starred",
	[FixedCategory.RECENT]: "recent",
	[FixedCategory.WORKSPACE]: "workspace",
};

const CategoryIcon: { [key: string]: IconDefinition } = {
	[FixedCategory.ALL]: faFileAlt,
	[FixedCategory.STARRED]: faStar,
	[FixedCategory.RECENT]: faClock,
	[FixedCategory.MY]: faUser,
	[FixedCategory.WORKSPACE]: faBuilding,
};

export const PathToCategory: { [key: string]: FixedCategory } = {
	[CategoryToPath[FixedCategory.ALL]]: FixedCategory.ALL,
	[CategoryToPath[FixedCategory.MY]]: FixedCategory.MY,
	[CategoryToPath[FixedCategory.STARRED]]: FixedCategory.STARRED,
	[CategoryToPath[FixedCategory.RECENT]]: FixedCategory.RECENT,
	[CategoryToPath[FixedCategory.WORKSPACE]]: FixedCategory.WORKSPACE,
};

export type Category = FixedCategory | string;

const tagOrdering: Partial<Record<string, number>> = {
	Meetings: 1,
	"1-on-1s": 2,
	"Expert-Approved": 3,
	Remote: 4,
};

function CategoryItem({
	category,
	onSelect,
	isSelected,
}: {
	category: Category;
	onSelect: (selection: Category) => void;
	isSelected?: boolean;
}) {
	const onClick = React.useCallback(() => {
		onSelect(category);
	}, [onSelect, category]);

	const icon = CategoryIcon[category.toString()];

	return (
		<button
			title={category.toString()}
			onClick={onClick}
			className={cx(styles.categoryItem, { [styles.categoryItemActive]: isSelected })}
		>
			{icon && <FontAwesomeIcon fixedWidth icon={icon} />}
			{category}
		</button>
	);
}

function CategorySection({
	categories,
	onSelect,
	selectedCategory,
}: {
	categories: readonly Category[];
	onSelect: (selection: Category) => void;
	selectedCategory?: Category;
}) {
	return (
		<section className={styles.categorySection}>
			{categories.map(c => (
				<CategoryItem key={c} category={c} onSelect={onSelect} isSelected={c == selectedCategory} />
			))}
		</section>
	);
}

const generalCategories = [FixedCategory.ALL, FixedCategory.STARRED, FixedCategory.MY, FixedCategory.WORKSPACE];
const generalCategoriesWithRecent = generalCategories.concat([FixedCategory.RECENT]);

export const CategoryList: React.FC<{
	showRecent?: boolean;
	selectedCategory?: Category;
	onSelectionChange: (category: Category) => void;
	tagCategories: readonly string[];
	search: string;
	onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onSearchClear: () => void;
	onSearchKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	dropdownViewOnMobile?: boolean;
}> = ({
	showRecent,
	selectedCategory,
	onSelectionChange,
	tagCategories,
	search,
	onSearchChange,
	onSearchClear,
	onSearchKeyDown,
	dropdownViewOnMobile,
}) => {
	const { isMobile } = useContext(ViewportContext);

	const sortedTags = [...tagCategories].sort((a, b) => {
		// Categories with explicit ordering are first and are ordered by
		// tagOrdering, the rest are sorted alphabetically after those ones.
		const aOrder = tagOrdering[a],
			bOrder = tagOrdering[b];
		if (!aOrder && !bOrder) return a < b ? -1 : 1;
		if (!aOrder) return 1;
		if (!bOrder) return -1;
		return aOrder - bOrder;
	});

	if (isMobile && dropdownViewOnMobile) {
		return (
			<div className={styles.mobileContainer}>
				<div className={styles.mobileLabel}>Viewing:</div>
				<ArrowDropdown
					className={styles.mobileDropdown}
					heading="Category"
					text={selectedCategory ?? FixedCategory.ALL}
				>
					{([] as Category[])
						.concat(showRecent ? generalCategoriesWithRecent : generalCategories)
						.concat(sortedTags)
						.map(tag => (
							// eslint-disable-next-line react/jsx-no-bind
							<DropdownItem key={tag} onClick={() => onSelectionChange(tag)} selected={selectedCategory === tag}>
								{tag}
							</DropdownItem>
						))}
				</ArrowDropdown>
			</div>
		);
	}

	return (
		<div className={styles.categoryList}>
			{!isMobile && (
				<SearchBox value={search} onChange={onSearchChange} onClear={onSearchClear} onKeyDown={onSearchKeyDown} />
			)}
			<CategorySection
				onSelect={onSelectionChange}
				selectedCategory={selectedCategory}
				categories={showRecent ? generalCategoriesWithRecent : generalCategories}
			/>
			<CategorySection onSelect={onSelectionChange} selectedCategory={selectedCategory} categories={sortedTags} />
		</div>
	);
};
